import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LogIn, Menu } from "lucide-react";
import { useTranslation } from "react-i18next";
import MobileMenuDrawer from "./MobileMenuDrawer";

/**
 * MobileStickyBar — slides down from top once user scrolls past the hero.
 *  [Login (left)]  [Velcor logo, center]  [☰ Menu (right)]
 *
 * Tapping ☰ opens the shared full-screen MobileMenuDrawer (same as Navbar).
 *
 * Hidden on /admin and /portal routes, and on >=md viewports.
 */
export default function MobileStickyBar() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const [show, setShow] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 320);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (pathname.startsWith("/admin") || pathname.startsWith("/portal")) return null;

  const lang = i18n.language?.startsWith("es") ? "es" : "en";

  return (
    <>
      <div
        className={`md:hidden fixed top-0 inset-x-0 z-[55] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          show ? "translate-y-0" : "-translate-y-full"
        }`}
        aria-hidden={!show}
        data-testid="mobile-sticky-bar"
      >
        <div
          className="mx-3 mt-3 rounded-full bg-white/95 backdrop-blur-md ring-1 ring-slate-200 shadow-[0_8px_30px_rgba(15,23,42,0.10)] px-2 h-12 flex items-center justify-between"
          style={{ paddingTop: "env(safe-area-inset-top)" }}
        >
          {/* LEFT — Login */}
          <Link
            to="/portal/login"
            className="inline-flex items-center gap-1.5 rounded-full bg-velcor-green/10 text-velcor-green px-3 h-9 text-[12.5px] font-extrabold hover:bg-velcor-green/20 transition-colors active:scale-95"
            data-testid="msb-login"
          >
            <LogIn className="w-3.5 h-3.5" strokeWidth={2.6} />
            {lang === "es" ? "Acceder" : "Sign in"}
          </Link>

          {/* CENTER — Logo only */}
          <Link
            to="/"
            className="absolute left-1/2 -translate-x-1/2 inline-flex items-center select-none"
            aria-label="Velcor Energy"
            data-testid="msb-brand"
          >
            <img src="/velcor-logo.png" alt="Velcor" className="w-7 h-7 object-contain" />
          </Link>

          {/* RIGHT — Menu trigger */}
          <button
            onClick={() => setOpen(true)}
            aria-label="Abrir menú"
            className="grid place-items-center w-9 h-9 rounded-full hover:bg-slate-100 transition-colors active:scale-95"
            data-testid="msb-menu-toggle"
          >
            <Menu className="w-5 h-5 text-velcor-ink" strokeWidth={2.4} />
          </button>
        </div>
      </div>

      {/* Shared full-screen drawer */}
      <MobileMenuDrawer open={open} onClose={() => setOpen(false)} testidPrefix="msb" />
    </>
  );
}
